import React, { ChangeEvent, FC, useContext, useEffect, useRef, useState } from "react";

import countries from "../../../utils/countries";
import { useLayout } from "../../hooks/use-layout";

import { Button, ButtonVariant, Checkbox, LayoutGrid, Section, Theme } from "@in/component-library";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";
import { AppContext } from "../../../app-context";
import { DEFAULT_COUNTRY_CODE } from "../../../utils/constants";
import { cleanupPhoneNumber, formatPhoneNumber } from "../../../utils/phoneNumberFormat";
import { updateContact } from "../../apis/update-contact";
import { updateProfile } from "../../apis/update-profile";
import { ContactUpdate } from "../../models/contact-update";
import UpdateEmail from "../update-email/update-email";
import UpdatePhone from "../update-phone/update-phone";
import UpdatePhoneConfirm from "../update-phone/update-phone-confirm";
import VerifyEmail from "../verify-email/verify-email";

interface ProfileFormProps {
  email: string;
  countryCode?: string;
  phoneNumber: string;
  newsletterConsent: boolean;
  hashedReturnUrl: string;
  returnToPreviousUrl: () => void;
  setProfile: (email: string, countryCode: string, phoneNumber: string, newsletterConsent: boolean) => void;
}

const ProfileForm: FC<ProfileFormProps> = ({
  email,
  countryCode = DEFAULT_COUNTRY_CODE,
  phoneNumber,
  newsletterConsent,
  hashedReturnUrl,
  returnToPreviousUrl,
  setProfile
}) => {
  const navigate = useNavigate();

  const { account } = useContext(AppContext);
  const { translations } = useLayout();

  const [currentEmail, setCurrentEmail] = useState<string>(email);
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState<string>(phoneNumber);
  const [currentCountryCode, setCurrentCountryCode] = useState<string>(countryCode);
  const [currentNewsletterConsent, setCurrentNewsletterConsent] = useState<boolean>(newsletterConsent);
  const emailBtnRef = useRef<HTMLButtonElement>(undefined);
  const phoneBtnRef = useRef<HTMLButtonElement>(undefined);
  const [displayEmailModal, setDisplayEmailModal] = useState<boolean>(false);
  const [displayVerifyEmailModal, setDisplayVerifyEmailModal] = useState<boolean>(false);
  const [displayPhoneModal, setDisplayPhoneModal] = useState<boolean>(false);
  const [displayConfirmPhoneModal, setDisplayConfirmPhoneModal] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>();
  const [phoneError, setPhoneError] = useState<string>();

  const submit = async (email: string, countryCode: string, phoneNumber: string, newsletterConsent: boolean) => {
    setIsSaving(true);
    setEmailError(undefined);
    setPhoneError(undefined);
    const cleanedPhoneNumber = cleanupPhoneNumber(phoneNumber);
    try {
      const response = await updateProfile({ email, countryCode, phoneNumber: cleanedPhoneNumber });

      if (response.code === 200) {
        setDisplayEmailModal(false);
        if (response.body.isVerified) {
          setProfile(email, countryCode, cleanedPhoneNumber, newsletterConsent);
          mutate("accounts/selected", response.body.account);
        } else {
          setDisplayVerifyEmailModal(true);
        }

        if (displayPhoneModal) {
          setDisplayPhoneModal(false);
          setDisplayConfirmPhoneModal(true);
          setProfile(email, countryCode, cleanedPhoneNumber, newsletterConsent);
        }
      } else if (response.code === 409) {
        setEmailError(translations.verifyEmailAddressAlreadyRegisteredError);
      } else {
        setEmailError(response.body.emailError);
        setPhoneError(response.body.phoneError);
      }
    } catch {
      setEmailError(translations.errorMessage);
    }

    setIsSaving(false);
  };

  const submitPhoneAndNewsletterConsent = async (
    countryCode: string,
    phoneNumber: string,
    newsletterConsent: boolean
  ) => {
    setIsSaving(true);
    setPhoneError(undefined);
    setCurrentNewsletterConsent(newsletterConsent);
    const cleanedPhoneNumber = cleanupPhoneNumber(phoneNumber);
    try {
      const response = await updateContact({
        phoneNumber: countryCode + cleanedPhoneNumber,
        newsLetterConsent: newsletterConsent
      } as ContactUpdate);
      if (response.code === 200) {
        setProfile(currentEmail, countryCode, cleanedPhoneNumber, newsletterConsent);
        setDisplayPhoneModal(false);
      } else {
        setPhoneError(response.body.phoneError);
      }
    } catch {
      setPhoneError(translations.errorMessage);
    }
    setIsSaving(false);
  };
  const onCloseModal = () => {
    setEmailError(undefined);
    setPhoneError(undefined);
  };

  const navigateBack = () => {
    account?.isVerified ? returnToPreviousUrl() : navigate(`/choose-account?returnUrl=${hashedReturnUrl}`);
  };

  useEffect(() => {
    document.title = `${account?.isVerified ? translations.verifiedHeader : translations.editContactHeader} - ${
      translations.applicationTitle
    } - ${translations.companyName}`;
  }, [account, translations]);

  const refreshPhone = () => {
    setCurrentPhoneNumber(phoneNumber);
    setCurrentCountryCode(countryCode);
  };

  return (
    <Section>
      <LayoutGrid>
        <div className="profile-form">
          <h1>{account?.isVerified ? translations.verifiedHeader : translations.editContactHeader}</h1>

          <div className="profile-form__prompt">
            <p>{translations.editContactLoggedInAsLabel}</p>
            <p>
              <span className="profile-form__name data-hj-suppress">{account?.name}</span>{" "}
              {!account?.isPersonal && (
                <>
                  <span>{translations.editContactForLabel}</span>{" "}
                  <span className="profile-form__company data-hj-suppress">{account?.companyName}</span>
                </>
              )}
            </p>
          </div>

          <div className="profile-form__email">
            <h2>{translations.chooseAccountEmailInput}</h2>

            <div className="profile-form__edit-email">
              <p className="data-hj-suppress">{currentEmail}</p>
              <Button
                id="edit-email"
                className="profile-form__edit-email-icon"
                type="button"
                theme={Theme.None}
                iconName="fa-light fa-edit"
                iconSize="lg"
                variant={ButtonVariant.Link}
                onClick={() => {
                  setDisplayEmailModal(true);
                }}
                aria-label={translations.editEmailBtn}
                innerRef={emailBtnRef}
              />

              <UpdateEmail
                displayEmailModal={displayEmailModal}
                email={currentEmail}
                updateEmail={(e: ChangeEvent<HTMLInputElement>) =>
                  setCurrentEmail((e.target as HTMLInputElement).value)
                }
                emailError={emailError}
                submit={() => submit(currentEmail, countryCode, phoneNumber, currentNewsletterConsent)}
                onClose={() => {
                  setDisplayEmailModal(false);
                  setCurrentEmail(email);
                  emailBtnRef.current?.focus();
                }}
                isSaving={isSaving}
              />

              <VerifyEmail
                displayVerifyEmailModal={displayVerifyEmailModal}
                resendCode={() => submit(currentEmail, countryCode, phoneNumber, currentNewsletterConsent)}
                onClose={() => {
                  setDisplayVerifyEmailModal(false);
                  setCurrentEmail(email);
                  emailBtnRef.current?.focus();
                }}
                email={currentEmail}
                updateEmail={() => {
                  setProfile(currentEmail, currentCountryCode, currentPhoneNumber, currentNewsletterConsent);
                  setCurrentNewsletterConsent(false);
                }}
              />
            </div>
          </div>

          <div className="profile-form__email">
            <h2>{translations.chooseAccountPhoneNumberLabel}</h2>
            <div className="profile-form__edit-phone">
              <p className="data-hj-suppress">
                {countryCode} {formatPhoneNumber(countryCode, phoneNumber)}
              </p>
              <Button
                className="profile-form__edit-phone-icon"
                type="button"
                theme={Theme.None}
                iconName="fa-light fa-edit"
                iconSize="lg"
                variant={ButtonVariant.Link}
                onClick={() => {
                  setDisplayPhoneModal(true);
                }}
                aria-label={translations.editPhoneNumberBtn}
                innerRef={phoneBtnRef}
              />

              <UpdatePhone
                phoneNumber={formatPhoneNumber(currentCountryCode, currentPhoneNumber)}
                phoneNumberError={phoneError}
                countryCode={currentCountryCode}
                isSaving={isSaving}
                onClose={() => {
                  setDisplayPhoneModal(false);
                  refreshPhone();
                  phoneBtnRef.current?.focus();
                }}
                displayPhoneModal={displayPhoneModal}
                submit={() =>
                  submitPhoneAndNewsletterConsent(currentCountryCode, currentPhoneNumber, currentNewsletterConsent)
                }
                updatePhoneNumber={(e: ChangeEvent<HTMLInputElement>) =>
                  setCurrentPhoneNumber(formatPhoneNumber(currentCountryCode, (e.target as HTMLInputElement).value))
                }
                updateCountryCode={(code: string) => setCurrentCountryCode(code || DEFAULT_COUNTRY_CODE)}
                countryCodeOptions={[
                  {
                    dialCode: DEFAULT_COUNTRY_CODE,
                    isoCode: "NO"
                  },
                  ...countries.sort((a, b) => a.isoCode.localeCompare(b.isoCode))
                ].map((e) => ({
                  value: e.dialCode,
                  text: `${e.isoCode} ${e.dialCode}`
                }))}
              />

              <UpdatePhoneConfirm
                displayConfirmPhoneModal={displayConfirmPhoneModal}
                onClose={() => {
                  setDisplayConfirmPhoneModal(false);
                  onCloseModal();
                  phoneBtnRef.current?.focus();
                }}
              />
            </div>
          </div>
          <div className="profile-form__phone">
            <h2 style={{ marginBottom: "5px" }}>{translations.editContactNewsletterConsentTitle}</h2>
            <Checkbox
              name="newsletterCheckbox"
              options={[
                {
                  selected: currentNewsletterConsent,
                  value: "newsletter",
                  text: translations.editContactNewsletterConsentLabel
                }
              ]}
              onChange={() =>
                submitPhoneAndNewsletterConsent(currentCountryCode, currentPhoneNumber, !currentNewsletterConsent)
              }
            />
          </div>
          <div className="profile-form__actions">
            <Button type="button" variant={ButtonVariant.Solid} theme={Theme.Neutral} onClick={navigateBack}>
              {translations.backBtnLabel}
            </Button>
          </div>
        </div>
      </LayoutGrid>
    </Section>
  );
};

export default ProfileForm;
